import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { useEffect, useRef, useState } from 'react';
import ReactLoading from 'react-loading';
import { useNavigate } from 'react-router-dom';
import { Schema } from '../../../../amplify/data/resource';
import { FundLogo } from '../../../components/custom/FundLogo';

const apiClient = generateClient<Schema>();

const SelectFund = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [funds, setFunds] = useState<any | null>({});
  const [loading, setLoading] = useState(false);

  const [selectedFund, setSelectedFund] = useState<string | null>(null);
  const fundItemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const handleFundClick = (fundName: string) => {
    setSelectedFund((prevSelectedFund) =>
      prevSelectedFund === fundName ? null : fundName,
    );
  };

  useEffect(() => {
    const fetchFunds = async () => {
      try {
        setLoading(true);
        const response = await apiClient.queries.getAgentFunds({});
        if (response && response.data) {
          if (typeof response.data === 'string') {
            const parsedResponse = JSON.parse(response.data);
            setTimeout(() => {
              setFunds(parsedResponse.body);
              setLoading(false);
            }, 1000);
          } else {
            console.error(
              'Error: response.data is not a string.',
              response.data,
            );
          }
        } else {
          console.error('Error: No funds data.');
        }
      } catch (error) {
        console.error('Error fetching funds data:', error);
      }
    };

    fetchFunds();
  }, []);

  return (
    <>
      {loading ? (
        <Stack justifyContent="center" alignItems="center" width="100%">
          <ReactLoading
            type={'bubbles'}
            color={theme.palette.green.main}
            height={250}
            width={250}
          />
        </Stack>
      ) : (
        <Grid direction={'row-reverse'} container spacing={2}>
          {Object.entries(funds).map(([fundName, products]) => (
            <Grid
              item
              key={fundName}
              xs={12}
              sm={6}
              md={4}
              lg={2}
              xl={2}
              ref={(el) => (fundItemRefs.current[fundName] = el)}
            >
              <FundItem
                fundName={fundName}
                products={products}
                isSelected={selectedFund === fundName}
                onClick={() => handleFundClick(fundName)}
                selectedFund={selectedFund}
              />
            </Grid>
          ))}
          {selectedFund && (
            <ProductGrid
              selectedFund={selectedFund}
              products={funds[selectedFund]}
              fundItemRefs={fundItemRefs}
            />
          )}
        </Grid>
      )}
    </>
  );
};

export default SelectFund;

const FundItem = ({
  fundName,
  products,
  isSelected,
  onClick,
  selectedFund,
}: {
  fundName: string;
  products: any;
  isSelected: boolean;
  onClick: () => void;
  selectedFund: string | null;
}) => {
  const navigate = useNavigate();

  let productName;

  if (Array.isArray(products) && products.length === 1) {
    productName = products[0]?.fundDetails?.productName;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: '#F2F4F7',
        width: '100%',
        height: '183px',
        borderRadius: '26px',
        boxSizing: 'border-box',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        transition: 'background-color 0.3s ease',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#E7ECF2',
          opacity: selectedFund === null ? 0 : isSelected ? 0 : 0.7,
          transition: 'opacity 0.3s ease',
          zIndex: 1,
        },
      }}
      onClick={() => {
        navigate(
          `/upload-file/generate-link?indexLogo=${fundName}&product=${products[0]?.fundDetails.productName.S}&fund=${products[0]?.fund}`,
        );
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '0',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
        }}
      >
        <NoFeeBadge />
      </Box>

      <FundLogo logoIndex={fundName} width="90%" height="90%" />

      {productName && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
          }}
        >
          <ProductBadge productName={productName} />
        </Box>
      )}
    </Box>
  );
};

const ProductGrid = ({
  selectedFund,
  products,
  fundItemRefs,
}: {
  selectedFund: any;
  products: any;
  fundItemRefs: any;
}) => {
  const theme = useTheme();
  const selectedFundRef = fundItemRefs.current[selectedFund];
  const [topPosition, setTopPosition] = useState('0px');
  const [productGridHeight, setProductGridHeight] = useState(0);
  const productGridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedFundRef) {
      const rect = selectedFundRef.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const gridHeight = productGridRef.current
        ? productGridRef.current.scrollHeight
        : 0;
      setProductGridHeight(gridHeight);

      const bottomSpace = windowHeight - rect.bottom;
      const topSpace = rect.top;

      if (bottomSpace >= gridHeight + 20) {
        setTopPosition(`${rect.bottom + 20}px`);
      } else if (topSpace >= gridHeight + 20) {
        setTopPosition(`${rect.top - gridHeight - 20}px`);
      } else if (bottomSpace > topSpace) {
        setTopPosition(`${rect.bottom + 20}px`);
      } else {
        setTopPosition(`${rect.top - gridHeight - 20}px`);
      }
    }
  }, [selectedFundRef, products, productGridHeight]);

  return (
    <Grid
      container
      spacing={2}
      sx={{
        position: 'absolute',
        top: topPosition,

        right: 0,
        width: '100%',
        zIndex: 1000,
        backgroundColor: 'transparent',
        padding: '16px',
      }}
      ref={productGridRef}
    >
      {Object.entries(products).map(([productName, productDetails]) => (
        <Grid item key={productName} xs={12} sm={6} md={3} lg={3}>
          <ProductItem
            productName={productName}
            productDetails={productDetails}
          />
        </Grid>
      ))}
    </Grid>
  );
};

const ProductItem = ({
  productName,
  productDetails,
}: {
  productName: string;
  productDetails: any;
}) => {
  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        p: 2,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      a
    </Box>
  );
};

const NoFeeBadge = () => {
  return (
    <div style={{ position: 'relative', width: '137px', height: '24px' }}>
      <svg
        width="137"
        height="24"
        viewBox="0 0 137 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10.4402 15.0996C11.393 16.2603 12.4542 17.3297 13.6077 18.2926C17.7037 21.7054 22.9674 23.763 28.7126 23.7731C32.2014 23.7792 35.6912 23.7854 39.18 23.7914C49.1611 23.8097 59.799 23.8278 69.7807 23.8451C79.9979 23.8634 89.5592 23.8805 99.7784 23.8979C102.548 23.9029 105.317 23.908 108.084 23.913C113.172 23.9222 117.883 22.3258 121.746 19.6C123.494 18.3716 125.064 16.9111 126.419 15.2648C126.94 14.6324 127.427 13.9726 127.88 13.2874C128.776 11.9383 129.537 10.4908 130.142 8.9664C130.34 8.46663 130.512 7.9548 130.66 7.43577C130.811 6.9037 131.129 5.95087 131.677 4.92507C132.133 4.08278 132.742 3.1897 133.551 2.44375C134.106 1.93504 134.69 1.50825 135.256 1.15542C135.877 0.769307 136.48 0.469214 137 0.241128C134.495 0.237236 131.989 0.232172 129.478 0.228086C120.834 0.212906 112.187 0.197534 103.54 0.182354C92.1204 0.162115 80.6985 0.141874 69.2786 0.122607C58.1234 0.102367 46.9706 0.0830993 35.8169 0.0628596C25.6453 0.045539 15.4727 0.0272452 5.30125 0.00914623C3.53456 0.00603243 1.76786 0.00291919 0 0C0.520978 0.230032 1.12116 0.532072 1.74042 0.920325C2.30694 1.2751 2.88883 1.70384 3.44133 2.21469C4.24003 2.95675 4.8299 3.84049 5.25961 4.67985C5.75626 5.64786 6.0402 6.55592 6.18402 7.1094C6.32297 7.64264 6.48313 8.17277 6.68378 8.68752C7.29506 10.2526 8.06728 11.7355 8.97963 13.1171C9.43268 13.8043 9.92019 14.4662 10.4402 15.0996Z"
          fill="#C2FE40"
        />
      </svg>
      <Typography
        fontWeight={'700'}
        fontSize={'14px'}
        fontFamily={'AlmoniDL'}
        color={'#343434'}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          whiteSpace: 'nowrap',
        }}
      >
        !ללא עלות
      </Typography>
    </div>
  );
};

const ProductBadge = ({ productName }: { productName: string }) => {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{
        position: 'relative',
        backgroundColor: '#E7ECF2',
        borderRadius: '20px',
        py: '5px',
        px: '10px',
      }}
    >
      <Typography
        fontWeight={'700'}
        fontSize={'12px'}
        fontFamily={'AlmoniDL'}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {productName}
      </Typography>
      <svg
        width="17"
        height="18"
        viewBox="0 0 17 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clip-path="url(#clip0_2709_4154)">
          <path
            d="M10.8049 2.23535L3.83219 6.2762C3.82799 6.27868 3.82589 6.28232 3.82589 6.2871C3.82407 7.28377 3.82374 8.28038 3.82488 9.27695C3.82498 9.34717 3.82684 9.38936 3.83047 9.40352C3.84173 9.44791 3.86946 9.48311 3.91365 9.50913C3.99765 9.55869 4.08394 9.60724 4.17252 9.65479"
            stroke="#9FBE5F"
            stroke-width="0.0286678"
          />
          <path
            d="M15.2605 4.90723C15.2926 4.99247 15.3087 5.07876 15.3088 5.1661C15.3093 9.24938 15.31 11.7092 15.3109 12.5456C15.3111 12.7757 15.2135 12.9474 15.018 13.0607C12.8443 14.3209 10.7072 15.5595 8.60665 16.7763C8.46844 16.8564 8.32774 16.876 8.18456 16.835C8.13483 16.8208 8.0494 16.7788 7.92827 16.7092C5.82452 15.4976 3.72076 14.286 1.61701 13.0744C1.58169 13.0541 1.53683 13.0194 1.48242 12.9704"
            stroke="#9FBE5F"
            stroke-width="0.0286678"
          />
          <path
            d="M10.8044 2.23726L3.83162 6.27811C3.82742 6.2806 3.82532 6.28423 3.82532 6.28901C3.82351 7.28568 3.82318 8.28229 3.82432 9.27886C3.82442 9.34908 3.82628 9.39127 3.82991 9.40543C3.84117 9.44982 3.8689 9.48502 3.91309 9.51104C3.99709 9.5606 4.08338 9.60915 4.17196 9.6567C4.17129 9.6501 4.172 9.65072 4.1741 9.65856C4.17433 9.65938 4.17471 9.66014 4.17523 9.66081C4.17574 9.66147 4.17638 9.66203 4.17711 9.66244C4.92984 10.0965 5.40734 10.3715 5.6096 10.4874C5.68792 10.5322 5.8049 10.4837 5.83697 10.4002C5.84432 10.3809 5.84799 10.338 5.84799 10.2717C5.84752 9.33152 5.84747 8.39193 5.84785 7.45295C5.84785 7.451 5.84836 7.44908 5.84935 7.44739C5.85033 7.4457 5.85174 7.4443 5.85343 7.44334L12.8268 3.40809C12.8311 3.4056 12.8355 3.4056 12.8399 3.40809C13.5739 3.83094 14.3046 4.25173 15.0318 4.67047C15.1275 4.72548 15.2037 4.80574 15.2606 4.91126C15.2927 4.9965 15.3088 5.08279 15.3089 5.17014C15.3094 9.25341 15.3101 11.7133 15.311 12.5497C15.3112 12.7798 15.2136 12.9514 15.0181 13.0647C12.8444 14.3249 10.7072 15.5635 8.60674 16.7804C8.46852 16.8604 8.32783 16.88 8.18465 16.8391C8.13492 16.8248 8.04949 16.7829 7.92836 16.7132C5.82461 15.5016 3.72085 14.29 1.6171 13.0785C1.58178 13.0581 1.53692 13.0234 1.48251 12.9744C1.42552 12.9044 1.3862 12.8274 1.36453 12.7434C1.35203 12.6946 1.34577 12.6103 1.34577 12.4906C1.34568 10.0802 1.34568 7.66988 1.34577 5.25954C1.34577 5.14694 1.35274 5.06667 1.36668 5.01874C1.39426 4.92403 1.44385 4.84329 1.51544 4.77651C1.5793 4.71682 1.65991 4.67377 1.73994 4.62742C3.81716 3.42344 5.89438 2.21952 7.9716 1.01564C8.06591 0.961013 8.13855 0.926956 8.18952 0.913467C8.297 0.884958 8.40247 0.888641 8.50594 0.924516C8.54126 0.936762 8.62549 0.981247 8.75865 1.05797C9.43645 1.4482 10.114 1.83881 10.7913 2.2298C10.7989 2.23411 10.8032 2.23659 10.8044 2.23726Z"
            fill="#808080"
          />
          <path
            d="M10.8049 2.23535L11.1478 2.74779C11.1523 2.75459 11.151 2.76004 11.144 2.76415L4.17667 6.80213C4.1755 6.80277 4.17453 6.80372 4.17385 6.80488C4.17317 6.80604 4.17281 6.80737 4.17281 6.80873L4.17252 9.65479C4.08394 9.60724 3.99765 9.55869 3.91365 9.50913C3.86946 9.48311 3.84173 9.44791 3.83047 9.40352C3.82684 9.38936 3.82498 9.34717 3.82488 9.27695C3.82374 8.28038 3.82407 7.28377 3.82589 6.2871C3.82589 6.28232 3.82799 6.27869 3.83219 6.2762L10.8049 2.23535Z"
            fill="#BDFB3E"
          />
          <path
            d="M15.2615 4.91016C15.3532 5.04514 15.4435 5.17979 15.5324 5.31411C15.5789 5.38424 15.6095 5.43924 15.6243 5.47914C15.6474 5.54123 15.659 5.60408 15.659 5.6677C15.6592 8.61244 15.6592 11.04 15.6591 12.9505C15.6591 13.0907 15.6531 13.1858 15.6409 13.2358C15.6048 13.3852 15.5156 13.5011 15.3733 13.5835C13.2843 14.7943 11.1953 16.005 9.10631 17.2158C8.97316 17.2929 8.88897 17.3378 8.85375 17.3505C8.69988 17.4062 8.54682 17.3902 8.39458 17.3025C6.2472 16.0659 4.09982 14.8292 1.95245 13.5925C1.92019 13.5741 1.88663 13.5471 1.85179 13.5115C1.82774 13.487 1.8013 13.4533 1.77247 13.4104C1.6753 13.2664 1.57895 13.1207 1.4834 12.9733C1.53781 13.0223 1.58267 13.057 1.61799 13.0774C3.72174 14.2889 5.8255 15.5005 7.92925 16.7121C8.05038 16.7818 8.13581 16.8237 8.18554 16.838C8.32872 16.8789 8.46941 16.8593 8.60763 16.7793C10.7081 15.5624 12.8453 14.3238 15.019 13.0636C15.2145 12.9503 15.3121 12.7787 15.3119 12.5486C15.311 11.7122 15.3102 9.25231 15.3098 5.16903C15.3097 5.08169 15.2936 4.9954 15.2615 4.91016Z"
            fill="#BDFB3E"
          />
        </g>
        <defs>
          <clipPath id="clip0_2709_4154">
            <rect
              width="15.4197"
              height="17"
              fill="white"
              transform="translate(0.624023 0.87207)"
            />
          </clipPath>
        </defs>
      </svg>
    </Stack>
  );
};
